import {
  DndContext,
  DragEndEvent,
  DraggableAttributes,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { styled } from '@mui/material';
import { compact } from 'lodash';
import React, { PropsWithChildren } from 'react';

const DragHandleContext = React.createContext<{
  listeners: SyntheticListenerMap | undefined;
  attributes: DraggableAttributes;
} | null>(null);

type DragHandleProps = React.HTMLAttributes<HTMLDivElement> & {
  isFlexItem?: boolean;
};

const DragHandleDiv = styled('div')<{ isFlexItem?: boolean }>(
  ({ isFlexItem }) => ({
    ...(isFlexItem && { display: 'flex' }),
  }),
);

const DragHandle: React.FC<DragHandleProps> = ({
  children,
  isFlexItem,
  ...rest
}) => {
  const ctx = React.useContext(DragHandleContext);
  if (!ctx) throw new Error('DragHandle must be used within DraggableItem');
  return (
    <DragHandleDiv
      {...ctx.listeners}
      {...ctx.attributes}
      isFlexItem={isFlexItem}
      {...rest}
    >
      {children}
    </DragHandleDiv>
  );
};

DragHandle.displayName = 'DraggableList.DragHandle';

type DraggableItemProps = PropsWithChildren<{
  id: string;
  className?: string;
  withDragHandle?: boolean;
}>;

const DraggableItem: React.FC<DraggableItemProps> = ({
  children,
  id,
  className,
  withDragHandle,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging && { position: 'relative' as const, zIndex: 1 }),
  };

  const dragProps = withDragHandle ? {} : { ...attributes, ...listeners };

  return (
    <DragHandleContext.Provider value={{ listeners, attributes }}>
      <div className={className} ref={setNodeRef} style={style} {...dragProps}>
        {children}
      </div>
    </DragHandleContext.Provider>
  );
};

DraggableItem.displayName = 'DraggableList.Item';

type Props = {
  onReorder: (output: {
    orderedItemIds: Array<string>;
    oldIndex: number;
    newIndex: number;
  }) => void;
  children:
    | React.ReactElement<DraggableItemProps>
    | Array<React.ReactElement<DraggableItemProps>>;
  className?: string;
  disabled?: boolean;
};

type DraggableListType = React.FC<Props> & {
  Item: React.FC<DraggableItemProps>;
  Handle: React.FC<DragHandleProps>;
};

const DraggableList: DraggableListType = ({
  children,
  onReorder,
  className,
  disabled,
}) => {
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
  );

  const items = compact(
    React.Children.map(children, (element) => {
      if (!React.isValidElement(element)) return null;
      return element.props.id;
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over?.id && active.id !== over.id) {
      const oldIndex = items.indexOf(active.id.toString());
      const newIndex = items.indexOf(over.id.toString());
      const orderedItemIds = arrayMove(items, oldIndex, newIndex);

      return onReorder({ orderedItemIds, oldIndex, newIndex });
    }
  };

  return (
    <DndContext
      onDragEnd={disabled ? undefined : handleDragEnd}
      sensors={disabled ? [] : sensors}
    >
      <SortableContext
        items={items}
        strategy={disabled ? undefined : verticalListSortingStrategy}
      >
        <div className={className}>{children}</div>
      </SortableContext>
    </DndContext>
  );
};

DraggableList.Item = DraggableItem;
DraggableList.Handle = DragHandle;

export default DraggableList;
